import { useEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconInfoCircleOutline } from '@iwoca/icons';
import { Label, Link, Pill, TextArea } from '@iwoca/orion';
import { FormProvider, useForm } from 'react-hook-form';

import styles from './CompanyQuestionAI.module.css';
import {
  TQuestionHistory,
  useCompanyQuestionSession,
} from './useCompanyQuestionSession';
import { useIsInStep0EligibilityTest } from '../../AnonymousSplitTest/useIsInStep0EligibilityTest';
import { useCompanyQuestionMutation } from '../../api/apiCompanyQuestion/apiCompanyQuestionMutation';
import { useGetCompanyQuestion } from '../../api/apiCompanyQuestion/apiCompanyQuestionQuery';
import { TCompanyQuestionRequest } from '../../api/apiCompanyQuestion/fetchPostCompanyQuestion';
import {
  useGetApplicationQuery,
  useGetStateQuery,
  useGetUserConsent,
} from '../../api/lendingApi/lendingApiQueries';
import { useUserConsentMutation } from '../../api/userConsentApi/useUserConsentMutation';
import { useSignupRouterUK } from '../../hooks/useSignupRouter/useSignupRouterUK';
import { TGetStateResponse } from '../../types';
import { getAmplitudeDefaultEventProperties } from '../../utils/amplitudeEvents';
import logger from '../../utils/logger';
import { Button } from '../Button/Button';
import RouteSpinner from '../RouteSpinner/RouteSpinner';

type TCompanyQuestionAIData = {
  answer: string;
  sicCode: string[];
};

const FALLBACK_QUESTION =
  'What products or services does your business sell, and who are your typical customers?';

const normaliseCompanyQuestion = (
  history: TQuestionHistory[],
  sicCodes?: string[],
  incorporationDate?: string,
): TCompanyQuestionRequest => {
  return {
    context: {
      sic_code: sicCodes || [],
      incorporation_date: incorporationDate || '',
    },
    history,
  };
};

export const USER_CONSENT_PROVIDER = 'signup_ai_company_questions';

const CompanyQuestionAI = () => {
  const maxTextLength = 500;

  const [textCount, setTextCount] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState('');

  const { application } = useGetApplicationQuery();
  const { state } = useGetStateQuery<TGetStateResponse>();

  const {
    sessionData,
    sicCodes,
    showThankYou,
    forceThankYou,
    isSessionHistoryLoading,
  } = useCompanyQuestionSession();

  const { postCompanyQuestion, isLoading } = useCompanyQuestionMutation();

  const {
    initialQuestion,
    isInitialCompanyQuestionLoading,
    isInitialCompanyQuestionError,
    isInitialCompanyQuestionSuccess,
  } = useGetCompanyQuestion({
    contextData: {
      sic_code: sicCodes,
      incorporation_date: state?.application?.company?.trading_from_date,
    },
    enabled: !isSessionHistoryLoading && sessionData.questions.length === 0,
  });

  const { navigateSignup } = useSignupRouterUK();
  const isInStep0EligibilityTest = useIsInStep0EligibilityTest();

  const methods = useForm<TCompanyQuestionAIData>();
  const { handleSubmit, register, reset } = methods;

  const { userConsent } = useGetUserConsent(USER_CONSENT_PROVIDER);
  const { postUserConsent } = useUserConsentMutation();

  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Company Question AI',
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  useEffect(() => {
    if (isSessionHistoryLoading) return;

    if (sessionData.nextQuestion) {
      return setCurrentQuestion(sessionData.nextQuestion);
    }

    if (initialQuestion && sessionData.questions.length === 0) {
      return setCurrentQuestion(initialQuestion);
    }

    if (isInitialCompanyQuestionError || isInitialCompanyQuestionSuccess) {
      setCurrentQuestion(FALLBACK_QUESTION);
    }
  }, [
    initialQuestion,
    isInitialCompanyQuestionLoading,
    isInitialCompanyQuestionError,
    isInitialCompanyQuestionSuccess,
    sessionData,
    isSessionHistoryLoading,
  ]);

  const handleManageUserConsent = async () => {
    if (!userConsent) {
      await postUserConsent({
        body: {
          data: {
            bundle_hash: viteBuildHash,
          },
        },
        provider: USER_CONSENT_PROVIDER,
        stateKey: state?.state_key || '',
      });
    }
  };

  const onSubmit = async (data: TCompanyQuestionAIData) => {
    if (!application?.application_id) {
      return;
    }

    const updatedQuestionCount = sessionData.questions.length + 1;

    const newQuestionEntry: TQuestionHistory = {
      id: updatedQuestionCount,
      question: currentQuestion,
      answer: data.answer,
    };

    const updatedHistory = [...sessionData.questions, newQuestionEntry];

    try {
      const response = await postCompanyQuestion({
        applicationId: application.application_id,
        data: normaliseCompanyQuestion(
          updatedHistory,
          sicCodes,
          state.application?.company?.trading_from_date,
        ),
      });

      tracking.ampli.companyQuestionSubmitted(
        getAmplitudeDefaultEventProperties(),
      );

      if (!response.questions_complete && response.inference_question) {
        setCurrentQuestion(response.inference_question);
        reset();
        setTextCount(0);
      }

      if (sessionData.questions.length === 0) {
        await handleManageUserConsent().catch((error) => {
          logger.error('Failed to store user consent', { error });
        });
      }
    } catch (e) {
      // TODO: BE to handle 5xx error instead of FE allowing customers to fail silently and continue with the customer journey
      forceThankYou();

      logger.error('Failed to set ai question', {
        error: e,
      });
    }
  };

  if (isSessionHistoryLoading) {
    return <RouteSpinner />;
  }

  const shouldShowAIUI = !isInitialCompanyQuestionError;
  return (
    <>
      <div className={styles.container}>
        {shouldShowAIUI && <Pill>Powered by AI</Pill>}
        <h2 className={styles.heading}>
          Tell us a bit more about your business
        </h2>
        <p className={styles.description}>
          This will help us understand your business better and more accurately
          assess your application.
        </p>
        {shouldShowAIUI && (
          <>
            <div className={styles.info}>
              <IconInfoCircleOutline
                data-testid="infoIcon"
                width={16}
                height={16}
                className={styles.infoIcon}
              />
              <p className={styles.infoDescription}>
                There are a maximum of five AI generated questions and they
                should take around two minutes to complete.
              </p>
            </div>
          </>
        )}

        {sessionData.questions.length > 0 && (
          <div className={styles.previousQuestions}>
            {sessionData.questions.map((history) => (
              <div key={history.id} className={styles.previousQuestion}>
                <p className={styles.previousQuestion}>{history.question}</p>
                <p className={styles.previousAnswer}>{history.answer}</p>
              </div>
            ))}
          </div>
        )}

        {!showThankYou && (
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
              <div className={styles.formFieldContainer}>
                <div className={styles.formField}>
                  <Label htmlFor="questionAndAnswer">{currentQuestion}</Label>
                  <TextArea
                    key={sessionData.questions.length}
                    data-testid={'answer-textarea'}
                    id="questionAndAnswer"
                    placeholder="Type your answer here..."
                    maxLength={maxTextLength}
                    className={styles.questionTextArea}
                    {...register('answer', { required: true })}
                    onChange={(e) => setTextCount(e.currentTarget.value.length)}
                  />
                </div>
                <p
                  data-testid="character-count"
                  className={styles.characterCount}
                >
                  {textCount} / {maxTextLength} characters
                </p>
              </div>
              <Button
                className={styles.nextQuestionBtn}
                type="submit"
                disabled={isLoading}
                loading={isLoading}
              >
                Continue
              </Button>
              {shouldShowAIUI && sessionData.questions.length < 1 && (
                <p
                  data-testid="ai-privacy-policy"
                  className={styles.privacyPolicy}
                >
                  By clicking “Continue” you agree to our{' '}
                  <Link
                    href="https://www.iwoca.co.uk/compliance/customer-privacy-policy"
                    className={styles.privacyPolicyLink}
                  >
                    privacy policy
                  </Link>
                  .
                </p>
              )}
            </form>
          </FormProvider>
        )}
        {showThankYou && (
          <>
            <p className={styles.thankYouMessage}>
              Thanks for that information. You can now continue to the next step
              of your application.
            </p>

            <Button
              className={styles.nextQuestionBtn}
              onClick={() => {
                tracking.ampli.companyQuestionsCompleted(
                  getAmplitudeDefaultEventProperties(),
                );
                navigateSignup({ route: 'check-eligibility' });
              }}
            >
              {isInStep0EligibilityTest
                ? 'Continue to personal details'
                : 'Continue to check eligibility'}
            </Button>
          </>
        )}
      </div>
    </>
  );
};

export default CompanyQuestionAI;
